import { useCallback, useEffect, useState } from "react";
import {
  useSegmentedDataLazyQuery,
  SegmentationMethod,
  DriverMode,
  SegmentResponse,
  SegmentedDataQueryVariables,
} from "graphql/types/graphql";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { ApolloError } from "@apollo/client";
import { MAX_ROWS } from "../components/tables/TablePaginationWrapper";

interface UseSegmentDataResult {
  data: SegmentResponse[] | undefined;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  fetchData: (variables?: Partial<SegmentedDataQueryVariables>) => Promise<SegmentResponse[] | undefined>;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  page: number;
  rowsPerPage: number;
}

export const useSegmentData = (): UseSegmentDataResult => {
  const {
    tableName,
    segmentGroups,
    driver1Type,
    driver1NumeratorType,
    driver1Mode,
    driver2Type,
    driver2NumeratorType,
    driver2Mode,
    count1Column,
    count2Column,
  } = useHeadroomContext();

  const [cachedData, setCachedData] = useState<SegmentResponse[] | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [hasPreviousPage, setHasPreviousPage] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(MAX_ROWS);

  const offset = (page - 1) * rowsPerPage;

  const hasAllInputs =
    tableName &&
    segmentGroups.length > 0 &&
    segmentGroups.every((group) => group.type) &&
    count1Column &&
    ((driver1Type && driver1Mode) || (driver2Type && driver2Mode));

  const formattedSegmentGroups = segmentGroups.map((group) => ({
    type: group.type,
    method: group.method as SegmentationMethod,
    segments: group.segments.map((segment) => ({
      id: segment.id,
      minValue: segment.minValue,
      maxValue: segment.maxValue,
    })),
    rangeColumn: group.method === "MANUAL" ? group.type : undefined,
  }));

  const [fetchSegmentedData, { data, loading, error }] = useSegmentedDataLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!loading && !error && data?.segmentedData) {
      setCachedData(data.segmentedData.edges);
      setTotalCount(data.segmentedData.totalCount);
      setHasNextPage(data.segmentedData.hasNextPage);
      setHasPreviousPage(data.segmentedData.hasPreviousPage);
    }
  }, [loading, error, data, page, rowsPerPage, offset]);

  const fetchData = useCallback(
    async (variables?: Partial<SegmentedDataQueryVariables>) => {
      if (!hasAllInputs) {
        return undefined;
      }
      const result = await fetchSegmentedData({
        variables: {
          tableName,
          segmentGroups: formattedSegmentGroups,
          count1Column,
          count2Column: count2Column || null,
          driver1NumeratorColumn: driver1Mode === "PERCENTAGE" ? driver1NumeratorType : null,
          driver1DenominatorColumn: driver1Type,
          driver1Mode: driver1Mode as DriverMode,
          driver2NumeratorColumn: driver2Mode === "PERCENTAGE" ? driver2NumeratorType : null,
          driver2DenominatorColumn: driver2Type,
          driver2Mode: driver2Mode as DriverMode,
          limit: variables?.limit ?? rowsPerPage,
          offset: variables?.offset ?? offset,
          ...variables,
        },
      });
      return result.data?.segmentedData.edges;
    },
    [
      hasAllInputs,
      fetchSegmentedData,
      tableName,
      formattedSegmentGroups,
      count1Column,
      count2Column,
      driver1Mode,
      driver1NumeratorType,
      driver1Type,
      driver2Mode,
      driver2NumeratorType,
      driver2Type,
      rowsPerPage,
      offset,
    ]
  );

  const handleSetPage = useCallback(
    (newPage: number) => {
      const maxPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
      const boundedPage = Math.min(Math.max(1, newPage), maxPage);
      setPage(boundedPage);
    },
    [totalCount, rowsPerPage]
  );

  const handleSetRowsPerPage = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  return {
    data: data?.segmentedData?.edges ?? cachedData,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    loading,
    error,
    fetchData,
    setPage: handleSetPage,
    setRowsPerPage: handleSetRowsPerPage,
    page,
    rowsPerPage,
  };
};
